/**
 * The copy that was here before the catalogue was split by surface: the
 * archived screen, the help viewer, the marketing library and the composer.
 *
 * One half of `AGENT_COPY`. The shape and the rules are documented on the
 * barrel in `../agent-copy.ts`; this file only holds entries.
 */
export const EXISTING: Record<string, string> = {
	// -- recently deleted
	"Back to Agent": "返回智能体",
	"Recently deleted": "最近删除",
	"Restore workflows and chats. Restored workflows keep their nodes and history; schedules remain paused until you enable them.":
		"恢复工作流和对话。工作流的节点与历史记录会保留，调度需要恢复后手动启用。",
	"Search deleted items": "搜索已删除项目",
	"Search by name": "按名称搜索",
	"Could not load deleted items. Please refresh and try again.": "无法加载已删除项目，请刷新后重试。",
	"Could not restore this item. Check your brand permissions and try again.": "恢复失败，请检查品牌权限后重试。",
	"No deleted items": "没有已删除项目",
	"Untitled chat": "未命名对话",
	Restore: "恢复",
	Workflow: "工作流",
	Chat: "对话",

	// -- help
	"Back to Ondit": "返回 Ondit",
	"Ondit Agent help": "Ondit 智能体帮助",
	"Help topics": "帮助主题",
	"Contact support": "联系支持",
	"Chat and planning": "对话与编排",
	"Describe the goal in your brand’s Agent page. Review the generated nodes, parameters and connections, then test the workflow. Publishing steps must satisfy the brand’s publishing requirements.":
		"在品牌的智能体页面描述目标。检查生成的节点、参数和连接，再测试运行；涉及发布的流程需要满足品牌的发布条件。",
	Workflows: "工作流",
	Articles: "文章",
	Article: "文章",
	Chinese: "中文",
	English: "英文",
	"Create from a template or add nodes on the canvas. Saved workflows can be copied, archived and restored. Archiving pauses schedules; enable them again when needed after restoring.":
		"从模板创建或在画布中添加节点。保存后可以复制、归档和恢复。归档会暂停调度，恢复后请按需重新启用。",
	"Knowledge bases": "知识库",
	"Create a knowledge base, import documents and inspect chunks. Keyword retrieval is available immediately. Semantic retrieval requires an Asale embedding model configured by an administrator and a completed index.":
		"创建知识库、导入文档并检查分块。关键词检索可直接使用；语义检索需要管理员配置 Asale 向量模型并完成索引。",
	Schedules: "调度",
	"Set run times with Schedule above the workflow canvas. Tasks → Scheduled shows upcoming runs and the last 90 days of history. Schedules run the revision saved at setup; save again to include later canvas changes.":
		"在工作流画布上方点击“定时”设置运行时间。在“任务 → 已计划”查看未来运行和最近 90 天的历史记录。计划按设置时保存的版本执行；修改画布后需重新保存计划才能生效。",
	"Execution logs": "运行日志",
	"Filter executions by workflow, time, trigger and outcome. Open a run to inspect node outputs and the saved canvas snapshot. Unavailable costs remain blank; credits come from actual model and tool charges.":
		"按工作流、时间、触发方式和结果筛选运行。打开详情查看节点输出和当时的画布快照。未取得的费用显示为空，积分取自实际模型和工具扣费。",
	"Integrations and availability": "集成与可用能力",
	"An integration in the catalog does not necessarily have a Rust executor yet. Unconnected third-party authorization, tools and advanced control nodes refuse execution. Use the execution error to identify the required capability.":
		"集成目录中的定义不代表 Rust 后端已经支持执行。尚未接通的第三方授权、工具或高级控制节点会拒绝运行；请根据运行错误调整流程。",

	// -- the composer's model and thinking chips
	Automatic: "自动",
	"Unable to load models": "模型列表加载失败",
	"Loading models…": "正在加载模型…",
	"Search models": "搜索模型",
	"No model matches": "没有匹配的模型",
	Model: "模型",
	Thinking: "思考",
	Off: "关闭",
	"Answer directly": "直接作答",
	Low: "低",
	"A moment to plan": "略作规划",
	Medium: "中",
	"Everyday work": "日常工作",
	High: "高",
	"Work through it": "复杂任务",

	// -- agent templates and content plans
	"Agent templates": "智能体模板",
	Analyse: "分析",
	"Content plans": "内容计划",
	"Asale model": "Asale 模型",
	" · No online supply": " · 暂无在线供应",
	"Credit limit": "积分上限",
	"Creates an editable workflow and runs it. Model usage is charged against this limit; outputs are drafts for review.":
		"创建可编辑工作流并运行。模型调用按实际用量计费，受上述积分上限约束；产出为待审核草稿。",
	"Run template": "运行模板",
	"Edit a copy": "编辑副本",
	"Back to details": "返回详情",
	"Opening…": "正在打开…",
	"Create and run": "创建并运行",
	"Running…": "运行中…",
	"Read-only access": "当前为只读权限",
	"Run result": "运行结果",
	Status: "状态",
	credits: "积分",
	"Open workflow": "打开工作流",
	"This model has no online supply. Choose another Asale model.": "该模型暂无在线供应，请选择其他 Asale 模型。",
	"The model returned an empty response. This run did not produce a result.": "模型返回空响应，本次运行未产生结果。",
	"Refresh result": "刷新运行结果",
	"View document": "查看文稿",
	"View structured result": "查看结构化结果",
	"Plan title": "计划名称",
	Objective: "目标",
	Audience: "受众",
	"Create plan": "创建计划",
	"All plans": "全部计划",
	"Plan actions": "计划操作",
	Pause: "暂停",
	Resume: "恢复",
	"Complete plan": "完成计划",
	"Add action": "添加行动",
	"Evidence and generation": "证据与内容生成",
	"Open task": "打开任务",
	"Manage action": "管理行动",
	"Next run": "下次运行",
	Evidence: "证据",
	"New plan": "新建计划",
	"No content plans yet.": "暂无内容计划。",
	View: "查看",
	"Save note": "保存备注",
	"Record outcome and complete": "记录结果并完成",
	Reopen: "重新打开",
	"Link reviewed page": "关联已审核页面",
	"Clear failed attempt": "清除失败任务",
	"Cancel action": "取消行动",
	"Outcome or note": "结果或备注",
	"Record the actual outcome before marking an action complete.": "标记完成前，请记录实际结果。",
	"Ondit page ID": "Ondit 页面 ID",
	"Cancel this action and stop its scheduled work.": "取消此行动及其后续调度。",
	"Action title": "行动名称",
	Stage: "阶段",
	Research: "研究",
	Facts: "事实",
	Create: "创作",
	Update: "更新",
	Distribute: "分发",
	Observe: "观测",
	Destination: "目标渠道",

	// -- plan and run states, as the marketing screens spell them
	Active: "进行中",
	Paused: "已暂停",
	Completed: "已完成",
	Pending: "待处理",
	Running: "运行中",
	Done: "已完成",
	"In review": "待审核",
	Cancelled: "已取消",
	Queued: "排队中",
	Failed: "失败",
	Skipped: "已跳过",
	Stopped: "已停止",

	// -- words more than one Agent screen needs
	Action: "操作",
	Cancel: "取消",
	Close: "关闭",
	Confirm: "确认",
	Dialog: "对话框",
	"Loading…": "加载中…",
	Name: "名称",
	Save: "保存",
	Type: "类型",
};
